/**
 * ONE `/sfmc-blocks` PULL PER SESSION (2026-08-19).
 *
 * The block library is now read by TWO surfaces, not one:
 *   • the Creative Studio, which registers the blocks and assembles a template
 *     from them (`StudioView`);
 *   • the Templates tab of the picker, which lists the `ECH_TPL_*` templates the
 *     same payload declares (`MvpTemplateGallery`, via `useEchTemplateList`).
 *
 * That pull is the slowest call in the app — 40–60s cold — so it must happen
 * exactly once. The promise is cached at module scope and handed to every caller;
 * a caller that arrives mid-flight waits on the SAME request rather than starting
 * a second one.
 *
 * WHAT IS DELIBERATELY *NOT* CACHED IN MEMORY: the work done ON the payload.
 * `registerEchBlocks()` + `echLoadTemplates()` still run per caller, per mount —
 * now via `echAdopt()`, which does them only when the payload's BYTES have changed.
 * `echLoadTemplates` bumping `echTplVersion()` is the signal `StudioView` uses to
 * rebuild an open document on fresher markup and carry the user's edits across
 * (`echRebuildKeepEdits`, audit row 20), so suppressing that bump on a genuinely
 * newer payload would silently kill the path. See `echSnap.ts` rule 3.
 *
 * ONE PULL PER SESSION IS NOT ENOUGH ON ITS OWN (2026-08-19).
 * The first pull of the session still costs 16–60s, and the Studio cannot assemble
 * anything until it lands. So every successful response is also written to
 * `localStorage` here — the ONE place that owns the network — and the next session
 * adopts it synchronously before first paint (`echSnap.ts`). This is the write half;
 * the read half is `echAdoptSnapshot()`.
 */
import { getSfmcBlocks } from "./api";
import { echSnapSave } from "./echSnap";
import type { SfmcBlock } from "./sfmcBlocks";

let inflight: Promise<SfmcBlock[]> | null = null;

/** The session's single `/sfmc-blocks` response. Rejects like the underlying
 *  call, and drops the cache on failure so a retry is a real retry. */
export function sfmcBlocksOnce(): Promise<SfmcBlock[]> {
  if (!inflight) {
    inflight = getSfmcBlocks()
      .then((res) => {
        const blocks = res.blocks ?? [];
        /* the snapshot is written from the RESOLVED response only — a rejection
           must leave the last good payload exactly where it is */
        echSnapSave(blocks);
        return blocks;
      })
      .catch((e) => { inflight = null; throw e; });
  }
  return inflight;
}

/** test seam / explicit retry: forget the cached pull */
export function __resetSfmcSource(): void { inflight = null; }

/* "I JUST CHANGED IT IN CONTENT BUILDER" (2026-08-19) — Layne needs a way to see an edit
   NOW rather than learn which caches are warm. Drops all three layers in order: the
   session's in-flight promise, the localStorage snapshot, and the worker's R2 object
   (via ?rebuild=1), then re-pulls. Without this the shortest honest wait is the worker
   TTL, and "wait a minute and refresh" is not an instruction anyone should need. */
export async function sfmcBlocksRefresh(): Promise<SfmcBlock[]> {
  inflight = null;
  const { __resetEchSnap } = await import("./echSnap");
  __resetEchSnap();
  inflight = getSfmcBlocks({ rebuild: true })
    .then((res) => { const blocks = res.blocks ?? []; echSnapSave(blocks); return blocks; })
    .catch((e) => { inflight = null; throw e; });
  return inflight;
}

/* ── AN OPEN EDITOR FOLLOWS CONTENT BUILDER ON ITS OWN (2026-09-12) ─────────────────
   Justin, on the standup chat (09-11): "I had one open when I updated the padding on the
   community modules … it did not update it till I hit refresh from SFMC." Layne: "I believe
   it auto updates" — it did not: `sfmcBlocksOnce` is ONE pull per page, so an email already
   open never saw an edit until Refresh or a reload. Jack: "can we make it."
   The watcher re-pulls the list on a timer while something is subscribed (the Studio),
   only while the tab is visible, past the backend's 60s cache (`fresh=1`) but never the R2
   rebuild. It hands every payload to the subscriber; the byte gate in `echAdopt` decides
   whether anything actually changed, so identical markup costs one request and no render.
   One timer however many subscribers, and it stops when the last one leaves. */
export const SFMC_WATCH_MS = 60 * 1000;
type Sub = (blocks: SfmcBlock[]) => void;
const subs = new Set<Sub>();
let timer: ReturnType<typeof setInterval> | null = null;
let polling = false;

async function pollOnce(): Promise<void> {
  if (polling || !subs.size) return;
  if (typeof document !== "undefined" && document.hidden) return;   /* a hidden tab does not poll */
  polling = true;
  try {
    const res = await getSfmcBlocks({ fresh: true });
    const blocks = res.blocks ?? [];
    if (!blocks.length) return;
    echSnapSave(blocks);
    /* the session's single pull now answers with the fresher payload too */
    inflight = Promise.resolve(blocks);
    subs.forEach((cb) => { try { cb(blocks); } catch { /* one bad subscriber does not stop the others */ } });
  } catch {
    /* a failed poll changes nothing — the blocks in hand stay */
  } finally {
    polling = false;
  }
}

/** subscribe to background re-pulls; returns the unsubscribe */
export function sfmcBlocksWatch(cb: Sub, everyMs: number = SFMC_WATCH_MS): () => void {
  subs.add(cb);
  if (!timer) timer = setInterval(() => { void pollOnce(); }, everyMs);
  return () => {
    subs.delete(cb);
    if (!subs.size && timer) { clearInterval(timer); timer = null; }
  };
}

/** test seam: run one poll now */
export function __sfmcPollNow(): Promise<void> { return pollOnce(); }
